import { DEV_MEETING_ID, type ClientRole } from '@zcs/shared';
import { resolveZoomRole } from './resolveZoomRole';
import {
  ensureZoomSdkConfigured,
  getLastConfigError,
  isLikelyEmbeddedInZoom,
  shouldShowZoomDiagnostics,
  zoomSdk,
} from './zoomSdk';
import { PromiseTimeoutError, withTimeout } from './promise-timeout';

/** Zoom Apps user context status (@see guest mode docs). */
export type ZoomUserContextStatus = 'unauthenticated' | 'authenticated' | 'authorized';

/** Short codes shown inside Zoom when startup fails (no app details). */
export type ZoomAccessReason =
  | 'no-sdk'
  | 'config-failed'
  | 'unsupported-api'
  | 'no-app-context'
  | 'sdk-error'
  | 'timeout';

export type ZoomAccess =
  | { status: 'loading' }
  | { status: 'blocked'; inZoom: boolean; reason: ZoomAccessReason; detail?: string }
  | {
      status: 'ready';
      contextToken: string;
      userId: string;
      userRole: ClientRole;
      userContextStatus: ZoomUserContextStatus;
      /** Fallback when app context omits mid (panel typ); sent as meetingUUID on WS connect. */
      meetingUUID?: string;
    };

const SDK_CALL_TIMEOUT_MS = 10_000;
const RESOLVE_TIMEOUT_MS = 30_000;

function devBrowserFallback(): ZoomAccess {
  const userId = import.meta.env.VITE_DEV_USER_ID ?? 'dev-host';
  const userRole = (import.meta.env.VITE_DEV_USER_ROLE ?? 'host') as ClientRole;
  const meetingId = import.meta.env.VITE_DEV_MEETING_ID ?? DEV_MEETING_ID;
  return {
    status: 'ready',
    contextToken: `dev:${userId}:${userRole}:${meetingId}`,
    userId,
    userRole,
    userContextStatus: 'authorized',
  };
}

export function parseUserContextStatus(userCtx: { status?: string }): ZoomUserContextStatus {
  const s = userCtx.status?.trim().toLowerCase();
  if (s === 'authorized') return 'authorized';
  if (s === 'authenticated') return 'authenticated';
  return 'unauthenticated';
}

/** Read current Zoom user context status (after config). */
export async function fetchUserContextStatus(): Promise<ZoomUserContextStatus> {
  if (import.meta.env.DEV && !isLikelyEmbeddedInZoom()) {
    return 'authorized';
  }
  const config = await ensureZoomSdkConfigured();
  if (!config || (config.unsupportedApis ?? []).includes('getUserContext')) {
    return 'authorized';
  }
  try {
    const userCtx = await withTimeout(
      async () => zoomSdk.getUserContext(),
      SDK_CALL_TIMEOUT_MS,
      'getUserContext',
    );
    return parseUserContextStatus(userCtx as { status?: string });
  } catch {
    return 'unauthenticated';
  }
}

/** Meeting scope for relay connect — required when app context omits mid (common for participants). */
async function resolveMeetingScopeId(): Promise<string | undefined> {
  try {
    const meeting = await withTimeout(
      async () => zoomSdk.getMeetingUUID(),
      SDK_CALL_TIMEOUT_MS,
      'getMeetingUUID',
    );
    const uuid = (meeting as { meetingUUID?: string }).meetingUUID?.trim();
    if (uuid) return uuid;
  } catch {
    // Fall through to getMeetingContext.
  }

  try {
    const ctx = await withTimeout(
      async () => zoomSdk.getMeetingContext(),
      SDK_CALL_TIMEOUT_MS,
      'getMeetingContext',
    );
    const parent = (ctx as { parentUUID?: string }).parentUUID?.trim();
    if (parent) return parent;
    return (ctx as { meetingID?: string }).meetingID?.trim();
  } catch {
    return undefined;
  }
}

function resolveParticipantId(userCtx: {
  participantUUID?: string;
  participantId?: string;
}): string {
  return userCtx.participantUUID?.trim() || userCtx.participantId?.trim() || 'unknown';
}

function isMeetingLikeContext(context: string | undefined): boolean {
  if (!context) return true;
  const c = context.toLowerCase();
  return (
    c.includes('meeting') ||
    c.includes('webinar') ||
    c.includes('immersive') ||
    c === 'inmeetingchat'
  );
}

function blocked(
  inZoom: boolean,
  reason: ZoomAccessReason,
  detail?: string,
): ZoomAccess {
  return { status: 'blocked', inZoom, reason, detail };
}

async function resolveZoomAccessInner(): Promise<ZoomAccess> {
  const inZoom = shouldShowZoomDiagnostics();

  if (import.meta.env.DEV && !isLikelyEmbeddedInZoom()) {
    return devBrowserFallback();
  }

  const config = await ensureZoomSdkConfigured();
  if (!config) {
    return blocked(
      inZoom,
      'config-failed',
      getLastConfigError() ??
        'zoomSdk.config did not complete — check Production SDK capabilities (getRunningContext, getUserContext, getAppContext).',
    );
  }

  try {
    const unsupported = config.unsupportedApis ?? [];
    if (unsupported.includes('getAppContext')) {
      return blocked(
        inZoom,
        'unsupported-api',
        `getAppContext unsupported (${unsupported.join(', ')})`,
      );
    }

    let runningContext = config.runningContext;
    try {
      const running = await withTimeout(
        async () => zoomSdk.getRunningContext(),
        SDK_CALL_TIMEOUT_MS,
        'getRunningContext',
      );
      runningContext = running.context ?? runningContext;
    } catch {
      // config.runningContext is enough
    }

    if (!isMeetingLikeContext(runningContext)) {
      return blocked(inZoom, 'sdk-error', `context:${runningContext ?? 'unknown'}`);
    }

    const userCtx = await withTimeout(
      async () => zoomSdk.getUserContext(),
      SDK_CALL_TIMEOUT_MS,
      'getUserContext',
    );

    const appCtx = await withTimeout(
      async () => zoomSdk.getAppContext(),
      SDK_CALL_TIMEOUT_MS,
      'getAppContext',
    );
    const contextToken = appCtx.context?.trim();
    if (!contextToken) {
      return blocked(
        inZoom,
        'no-app-context',
        'getAppContext returned empty — re-add the app in Zoom after enabling getAppContext.',
      );
    }

    const meetingUUID = await resolveMeetingScopeId();
    if (!meetingUUID) {
      return blocked(
        inZoom,
        'sdk-error',
        'Could not read meeting id — enable getMeetingUUID and getMeetingContext in Zoom Marketplace, then re-add the app.',
      );
    }

    const userRole = await resolveZoomRole(userCtx);

    return {
      status: 'ready',
      contextToken,
      userId: resolveParticipantId(userCtx),
      userRole,
      userContextStatus: parseUserContextStatus(userCtx as { status?: string }),
      meetingUUID,
    };
  } catch (err) {
    if (err instanceof PromiseTimeoutError) {
      return blocked(inZoom, 'timeout', err.message);
    }
    const detail = err instanceof Error ? err.message : 'unknown';
    return blocked(inZoom, 'sdk-error', detail);
  }
}

/**
 * Fresh app context for relay $connect (Zoom tokens expire ~5 minutes).
 * Falls back to bootstrap token when not embedded or getAppContext is unavailable.
 */
export async function refreshZoomContextToken(fallback: string): Promise<string> {
  if (import.meta.env.DEV && !isLikelyEmbeddedInZoom()) {
    return fallback;
  }

  try {
    const config = await ensureZoomSdkConfigured();
    if (!config || (config.unsupportedApis ?? []).includes('getAppContext')) {
      return fallback;
    }
    const appCtx = await withTimeout(
      async () => zoomSdk.getAppContext(),
      SDK_CALL_TIMEOUT_MS,
      'getAppContext',
    );
    const fresh = appCtx.context?.trim();
    return fresh || fallback;
  } catch {
    return fallback;
  }
}

/** Resolve whether the panel may run and obtain Zoom context for the relay. */
export async function resolveZoomAccess(): Promise<ZoomAccess> {
  try {
    return await withTimeout(async () => resolveZoomAccessInner(), RESOLVE_TIMEOUT_MS, 'resolve');
  } catch (err) {
    if (err instanceof PromiseTimeoutError) {
      return blocked(shouldShowZoomDiagnostics(), 'timeout', err.message);
    }
    throw err;
  }
}
